// src/components/music/parts/AddToPlaylistModal.jsx

import { motion } from 'framer-motion';
import { X, Check, Plus, ListMusic } from 'lucide-react';

export const AddToPlaylistModal = ({
  track,
  playlists,
  isOpen,
  onClose,
  onAddToPlaylist,
  currentUser
}) => {
  if (!isOpen || !track || !currentUser) return null;

  // Check which playlists already contain this track
  const getPlaylistStatus = (playlist) => {
    const trackExists = playlist.tracks?.some(t => t.id === track.id);
    return trackExists;
  };

  // Filter playlists that don't already have this track
  const availablePlaylists = playlists.filter(p => !getPlaylistStatus(p));
  const playlistsWithTrack = playlists.filter(p => getPlaylistStatus(p));

  const handleAddToPlaylist = async (playlistId) => {
    await onAddToPlaylist(playlistId, track);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 font-sans antialiased"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 15 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 15 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-[#121318] border border-white/10 rounded-2xl shadow-2xl w-full max-w-md max-h-[75vh] overflow-hidden flex flex-col text-white relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Ambient Gold Glow */}
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#bd9f67]/15 rounded-full blur-2xl pointer-events-none" />

        {/* Header */}
        <div className="bg-[#181a22] p-5 border-b border-white/10 flex items-center justify-between relative z-10">
          <div className="flex-1 pr-3">
            <div className="flex items-center gap-1.5 text-[#bd9f67] text-[10px] font-bold tracking-wider uppercase mb-1">
             Playlist Selection
            </div>
            <h3 className="text-base font-bold text-white tracking-tight truncate">
              Add to Playlist
            </h3>
            <p className="text-xs text-white/50 truncate mt-0.5">
              {track.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-white/40 hover:text-white hover:bg-white/10 transition"
            aria-label="Close modal"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {playlists.length === 0 ? (
            <div className="text-center py-10 bg-black/20 rounded-xl border border-white/5">
              <div className="p-3 rounded-full bg-[#bd9f67]/10 text-[#bd9f67] w-fit mx-auto mb-3">
                <ListMusic size={24} />
              </div>
              <p className="text-xs font-semibold text-white/70">No playlists found</p>
              <p className="text-[11px] text-white/40 mt-1">Create a playlist in your library first</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Playlists that don't have this track */}
              {availablePlaylists.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-2.5">
                    Available Playlists
                  </p>
                  <div className="space-y-2">
                    {availablePlaylists.map((playlist) => (
                      <motion.button
                        key={playlist.id}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleAddToPlaylist(playlist.id)}
                        className="w-full text-left p-3.5 rounded-xl bg-[#181a22]/80 hover:bg-[#181a22] border border-white/5 hover:border-[#bd9f67]/40 transition duration-200 group flex items-center justify-between"
                      >
                        <div className="min-w-0 flex-1 pr-3">
                          <p className="text-xs font-bold text-white group-hover:text-[#bd9f67] transition-colors truncate">
                            {playlist.name}
                          </p>
                          <p className="text-[11px] text-white/40 mt-0.5">
                            {playlist.tracks?.length || 0} track
                            {playlist.tracks?.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="p-1.5 rounded-lg bg-white/5 group-hover:bg-[#bd9f67] text-white/60 group-hover:text-gray-950 transition flex-shrink-0">
                          <Plus size={16} />
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Playlists that already have this track */}
              {playlistsWithTrack.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-2.5 mt-2">
                    Already Added
                  </p>
                  <div className="space-y-2">
                    {playlistsWithTrack.map((playlist) => (
                      <div
                        key={playlist.id}
                        className="w-full text-left p-3.5 rounded-xl bg-[#bd9f67]/10 border border-[#bd9f67]/20 flex items-center justify-between opacity-70"
                      >
                        <div className="min-w-0 flex-1 pr-3">
                          <p className="text-xs font-bold text-[#bd9f67] truncate">
                            {playlist.name}
                          </p>
                          <p className="text-[11px] text-[#bd9f67]/70 mt-0.5">
                            {playlist.tracks?.length || 0} track
                            {playlist.tracks?.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="p-1.5 rounded-lg bg-[#bd9f67]/20 text-[#bd9f67] flex-shrink-0">
                          <Check size={16} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-[#181a22] border-t border-white/5">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-xl transition font-semibold text-xs uppercase tracking-wider"
          >
            Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};